import BaseApi from "./base-api";

/**
 * Class for the Cognito API
 *
 * @extends BaseApi
 * @description This class is used to interact with the Cognito API. This is used to get the user's groups and add them to the user.
 *
 * @note There are addional sungage methods in the proposal-tool-api.js file
 */
export class CognitoApi extends BaseApi {
  constructor() {
    super("cognito");

    this.methods = {
      GET_USER: "get_user",
      GET_USERS: "get_users",
      GET_ALL_USERS: "get_all_users",
      GET_GROUPS: "get_groups",
      LIST_USERS_IN_GROUP: "list_users_in_group",
      RESET_PASSWORD: "reset_password",
      CREATE_USER: "create_user",
      UPDATE_USER: "update_user",
      DISABLE_USER: "disable_user",
      ENABLE_USER: "enable_user",
      GET_USER_GROUPS: "get_user_groups",
      ADD_USER_TO_GROUP: "add_user_to_group",
      REMOVE_USER_FROM_GROUP: "remove_user_from_group",
      GLOBAL_SIGN_OUT: "global_sign_out",
      AUTHENTICATE: "authenticate",
    };
  }

  /**
   * Get a user by username
   *
   * @param {string} username - The username of the user to get
   * @returns {Promise<Object>} The user object
   */
  getUser = async ({ username }) => {
    const response = await this.request({
      method: this.methods.GET_USER,
      username,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not get user");
  };

  /**
   * Get all users
   *
   * @returns {Promise<Object>} The users object
   */
  getUsers = async ({ filter }) => {
    const response = await this.request({
      method: this.methods.GET_USERS,
      filter,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not get users");
  };

  /**
   * Get all users
   *
   * @returns {Promise<Object>} The users object
   */
  getAllUsers = async ({ filter }) => {
    const response = await this.request({
      method: this.methods.GET_ALL_USERS,
      filter,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not get all users");
  };

  /**
   * Get all groups
   *
   * @returns {Promise<Object>} The groups object
   */
  getGroups = async () => {
    const response = await this.request({
      method: this.methods.GET_GROUPS,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not get groups");
  };

  /**
   * Get all users in a group
   *
   * @param {string} group_name - The name of the group to get users from
   * @returns {Promise<Object>} The users object
   */
  listUsersInGroup = async ({ group_name }) => {
    const response = await this.request({
      method: this.methods.LIST_USERS_IN_GROUP,
      group_name,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not get users in group");
  };

  /**
   * Reset a user's password
   *
   * @param {string} username - The username of the user to reset the password for
   * @returns {Promise<Object>} The response object
   */
  resetPassword = async ({ username, password, permanent }) => {
    const response = await this.request({
      method: this.methods.RESET_PASSWORD,
      username,
      password,
      permanent,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not reset password");
  };

  /**
   * Create a user
   *
   * @param {Object} user - The user object
   * @returns {Promise<Object>} The response object
   */
  createUser = async ({ email, password, first_name, last_name }) => {
    const response = await this.request({
      method: this.methods.CREATE_USER,
      email,
      password,
      firstName: first_name,
      lastName: last_name,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not create user");
  };

  /**
   * Update a user
   *
   * @param {Object} data - The user object
   * @param {string} data.username - The username of the user to update
   * @param {Object} data.attributes - The attributes to update
   * @returns {Promise<Object>} The response object
   */
  updateUser = async ({ username, attributes }) => {
    const response = await this.request({
      method: this.methods.UPDATE_USER,
      username,
      attributes,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not update user");
  };

  /**
   * Disable a user
   *
   * @param {string} username - The username of the user to disable
   * @returns {Promise<Object>} The response object
   */
  disableUser = async ({ username }) => {
    const response = await this.request({
      method: this.methods.DISABLE_USER,
      username,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not disable user");
  };

  /**
   * Enable a user
   *
   * @param {string} username - The username of the user to enable
   * @returns {Promise<Object>} The response object
   */
  enableUser = async ({ username }) => {
    const response = await this.request({
      method: this.methods.ENABLE_USER,
      username,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not enable user");
  };

  /**
   * Get a user's groups
   *
   * @param {string} username - The username of the user to get groups for
   * @returns {Promise<Object>} The response object
   */
  getUserGroups = async ({ username }) => {
    const response = await this.request({
      method: this.methods.GET_USER_GROUPS,
      username,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not get user groups");
  };

  /**
   * Add a user to a group
   *
   * @param {string} username - The username of the user to add to a group
   * @param {string} group_name - The name of the group to add the user to
   * @returns {Promise<Object>} The response object
   */
  addUserToGroup = async ({ username, group_name }) => {
    const response = await this.request({
      method: this.methods.ADD_USER_TO_GROUP,
      username,
      group_name,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not add user to group");
  };

  /**
   * Remove a user from a group
   *
   * @param {string} username - The username of the user to remove from a group
   * @param {string} group_name - The name of the group to remove the user from
   * @returns {Promise<Object>} The response object
   */
  removeUserFromGroup = async ({ username, group_name }) => {
    const response = await this.request({
      method: this.methods.REMOVE_USER_FROM_GROUP,
      username,
      group_name,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not remove user from group");
  };

  /**
   * Global sign out
   *
   * @returns {Promise<Object>} The response object
   */
  globalSignOut = async ({ username }) => {
    const response = await this.request({
      method: this.methods.GLOBAL_SIGN_OUT,
      username,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not global sign out");
  };

  /**
   * Authenticate a user
   *
   * @param {string} username - The username of the user to authenticate
   * @param {string} password - The password of the user to authenticate
   * @returns {Promise<Object>} The response object
   */
  authenticate = async ({ username, password }) => {
    const response = await this.request({
      method: this.methods.AUTHENTICATE,
      username,
      password,
    });

    if (response.success) {
      return response;
    }

    throw new Error(response?.message || "Could not authenticate");
  };
}

export default new CognitoApi();
